const { Core, Utils, Cache } = Global;
const { $fetch, debounce, $toggleDisplay, $replaceContent, $active, $loading, setDocumentClickHandler } = Utils;

const RQ_SECTION = 'r_predictive-search';
const RQ_SECTION_SELECTOR = '[data-template]';
const ATTR_NO_RESULTS = 'data-empty-results';
const DEFAULT_MIN_CHARS = 3;
const DEFAULT_LIMIT = 5;
const DEBOUNCE_INTERVAL = 300;

customElements.define('predictive-search', class extends Core {
    propTypes = {
        'results-limit': Number
    }

    elements = {
        $: ['search-input', 'product-type-select', 'button', 'results', 'initial']
    }

    render() {
        this.$('search-input', {
            focus: this._handleFocus,
            input: debounce(this._handleInput.bind(this), DEBOUNCE_INTERVAL)
        });
        this.$('button', {
            click: this._handleButtonClick
        });
        this.$('product-type-select', {
            change: this._handleTypeSelect
        });
        this._toggleDocClickListener = setDocumentClickHandler(this._handleOuterClick.bind(this));
    }

    _handleTypeSelect() {
        this._handleInput();
        this.$('search-input').focus();
    }

    _handleButtonClick(e) {
        if (this.$('search-input').value) return;
        e.preventDefault();
        this.$('search-input').focus(); 
    }

    _handleFocus() {
        this.active = this.minQueryChars || !!this.$('initial');
    }

    async _handleInput() {
        if(!this.minQueryChars) {
            this.showInitial = true;
            this.showResults = false;
            return;
        }
        await this._getQueryResults();
        this._renderQueryResults();
        this.active = true;
    }

    async _getQueryResults() {
        if (Cache.has(this.query)) return;

        const inputValue = this.$('search-input').value.trim();
        let $queryDoc;

        if (inputValue.includes('-')) {
            // Term contains a dash (SKU-shaped). Try an exact-phrase match first,
            // since shopify_search tokenizes hyphenated terms and can bury/miss
            // the correct product in fuzzy results (e.g. "CTMB-AX-T").
            $queryDoc = await this._fetchQueryDoc(this._buildQuery(`"${inputValue}"`));

            if ($queryDoc.hasAttribute(ATTR_NO_RESULTS)) {
                // No exact match found - fall back to normal fuzzy search
                // so real hyphenated keyword searches still work as today.
                $queryDoc = await this._fetchQueryDoc(this.query);
            }
        } else {
            $queryDoc = await this._fetchQueryDoc(this.query);
        }

        Cache.set(this.query, {
            hasResults: !$queryDoc.hasAttribute(ATTR_NO_RESULTS),
            content: $queryDoc
        });
    }

    async _fetchQueryDoc(queryString) {
        return $fetch(`${routes.predictive_search_url}`, { 
            params: {
                'q': queryString,
                'resources[limit]': this.limit,
                'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
                'section_id': RQ_SECTION
            },
            before: this._loading(true),
            after: this._loading(false),
            select: RQ_SECTION_SELECTOR
        });
    }

    _renderQueryResults() {
        const currentQuery = Cache.get(this.query);

        $replaceContent(this.$('results'), currentQuery.content);

        this.showInitial = !currentQuery.hasResults;
        this.showResults = true;
    }

    _handleOuterClick(e) {
        if(!this.contains(e.target) && this.active && document.activeElement !== this.$('search-input')) {
            this.active = false;
        }
    }
    
    _loading(state) {
        return () => {
            $loading(this, state);
        }
    }
    
    set showInitial(state) {
        if(!this.$('initial')) {
            this.active = false;
            return;
        }

        $toggleDisplay(this.$('initial'), state);
        this.active = state;
    }

    set showResults(state) {
        $toggleDisplay(this.$('results'), state);
    }

    set active(state) {
        if (state === this._active) {
            return;
        }
        this._toggleDocClickListener(state)
        $active(this, state);
        this._active = state;
    }

    get active() {
        return !!this._active;
    }

    get minQueryChars() {
        return this.$('search-input').value.trim().length >= DEFAULT_MIN_CHARS;
    }

    _buildQuery(value) {
        if (this.$('product-type-select') && this.$('product-type-select').value !== "") {
            return `product_type:${this.$('product-type-select').value} AND ${value}`
        }
        return value;
    }

    get query() {
        const inputValue = this.$('search-input').value.trim();
        return this._buildQuery(inputValue);
    }
    
    get limit() {
        return this.prop('results-limit') || DEFAULT_LIMIT;
    }
});